/**
 * PublicProfilePage Component
 * Displays public user profiles with role-adaptive information
 * Layout: Profile header → Two columns (Bio/Info left, Gallery/Posts right)
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import Navbar from '../components/Navbar';
import Gallery from '../components/Gallery';
import ProfilePostCard from '../components/ProfilePostCard';
import '../styles/colors.css';

const PublicProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [user, setUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchUserProfile = async () => {
    try {
      const response = await apiClient.get(`/users/${id}`);
      const userData = response.data.user || response.data;
      console.log('User profile data:', userData);
      setUser(userData);
    } catch (err) {
      setError('Failed to load profile');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPosts = async () => {
    try {
      console.log('Fetching posts for user ID:', id);
      
      // Fetch all posts and filter for this user
      const [eventsRes, articlesRes, oppsRes] = await Promise.all([
        apiClient.get('/events'),
        apiClient.get('/articles'),
        apiClient.get('/opportunities')
      ]);
      
      const userId = parseInt(id);
      console.log('Looking for posts created by or collaborated on by user ID:', userId);
      
      // Events: created by user OR user is accepted collaborator
      const events = (eventsRes.data.events || eventsRes.data || [])
        .filter(e => {
          const isCreator = e.created_by === userId;
          const isCollaborator = e.collaborators && e.collaborators.some(
            c => c.user_id === userId && c.status === 'accepted'
          );
          return isCreator || isCollaborator;
        })
        .map(e => ({ ...e, post_type: 'event' }));
      
      // Articles: created by user OR user is accepted collaborator
      const articles = (articlesRes.data.articles || articlesRes.data || [])
        .filter(a => {
          const isCreator = a.created_by === userId;
          const isCollaborator = a.collaborators && a.collaborators.some(
            c => c.user_id === userId && c.status === 'accepted'
          );
          return isCreator || isCollaborator;
        })
        .map(a => ({ ...a, post_type: 'article' }));
      
      // Opportunities: created by user OR user is accepted collaborator
      const opportunities = (oppsRes.data.opportunities || oppsRes.data || [])
        .filter(o => {
          const isCreator = o.created_by === userId;
          const isCollaborator = o.collaborators && o.collaborators.some(
            c => c.user_id === userId && c.status === 'accepted'
          );
          return isCreator || isCollaborator;
        })
        .map(o => ({ ...o, post_type: 'opportunity' }));
      
      console.log('Filtered events (created + collaborated):', events);
      console.log('Filtered articles (created + collaborated):', articles);
      console.log('Filtered opportunities (created + collaborated):', opportunities);
      
      const allPosts = [...events, ...articles, ...opportunities];
      allPosts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      console.log('All posts combined:', allPosts);
      
      setPosts(allPosts);
    } catch (err) {
      console.error('Failed to load posts:', err);
    }
  };

  useEffect(() => {
    fetchUserProfile();
    fetchUserPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getRoleBadgeText = (role) => {
    const roleMap = {
      'band': 'Band/Artist',
      'venue': 'Venue',
      'promoter': 'Promoter',
      'photographer': 'Photographer',
      'vendor': 'Vendor',
      'studio': 'Recording Studio',
      'fan': 'Fan'
    };
    return roleMap[role] || role;
  };

  const formatVenueTypes = (types) => {
    if (!types || types.length === 0) return '';
    return types.map(t => t.charAt(0).toUpperCase() + t.slice(1)).join(', ');
  };

  const formatAgeRestriction = (age) => {
    if (age === 'all_ages') return 'All Ages';
    if (age === '18_plus') return '18+';
    if (age === '21_plus') return '21+';
    return age;
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div style={{ minHeight: '100vh', background: 'var(--bg-dark)', color: 'var(--text-light)', padding: '40px 20px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ fontSize: '18px' }}>Loading profile...</div>
        </div>
      </>
    );
  }

  if (error || !user) {
    return (
      <>
        <Navbar />
        <div style={{ minHeight: '100vh', background: 'var(--bg-dark)', color: 'var(--text-light)', padding: '40px 20px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', marginBottom: '16px', color: 'var(--accent-danger)' }}>Profile Not Found</div>
            <button onClick={() => navigate('/find')} className="btn btn-primary">Browse Users</button>
          </div>
        </div>
      </>
    );
  }

  const hasGalleryImages = user.image_gallery_urls && user.image_gallery_urls.length > 0;
  const hasPosts = posts && posts.length > 0;

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .profile-page-container {
            padding-top: 125px !important;
          }

          .profile-email-section {
            position: absolute !important;
            top: 85px !important;
            right: 20px !important;
            text-align: right !important;
          }

          .profile-header-main {
            margin-top: 30px !important;
          }

          .profile-two-column {
            display: flex !important;
            flex-direction: column !important;
          }

          .profile-info-text {
            font-size: 16px !important;
          }

          .profile-genre-tags {
            margin-top: 32px !important;
            padding-top: 24px !important;
            border-top: 1px solid var(--border-light) !important;
          }

          .profile-posts-grid {
            grid-template-columns: 1fr !important;
          }
        }

        .profile-gallery-hide-title {
          display: none !important;
        }

        .profile-gallery-section {
          align-self: flex-start !important;
        }
      `}</style>
      <Navbar />
      <div className="profile-page-container" style={{ minHeight: '100vh', background: 'var(--bg-dark)', paddingTop: '140px', paddingBottom: '40px', paddingLeft: '20px', paddingRight: '20px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative' }}>
          
          {/* Header Section - NO BOX */}
          <div className="profile-header-main" style={{ marginBottom: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px', flexWrap: 'wrap' }}>
              
              {/* Profile Image */}
              <div style={{ flexShrink: 0 }}>
                {user.profile_image_url ? (
                  <img 
                    src={user.profile_image_url} 
                    alt={user.display_name}
                    style={{ width: '120px', height: '120px', borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--accent-primary)' }}
                  />
                ) : (
                  <div style={{ width: '120px', height: '120px', borderRadius: '50%', background: 'var(--smoke-gray)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '48px', fontWeight: '700', color: 'var(--text-light)' }}>
                    {user.display_name.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>

              {/* Name, Role, Location */}
              <div style={{ flex: 1, minWidth: '250px' }}>
                <h1 style={{ margin: '0 0 12px 0', fontSize: '32px', fontWeight: '700', color: 'var(--text-light)' }}>
                  {user.display_name}
                </h1>
                
                <div style={{ marginBottom: '12px' }}>
                  <span className="badge badge-primary" style={{ fontSize: '13px' }}>
                    {getRoleBadgeText(user.account_role)}
                  </span>
                </div>

                {user.city && user.state && (
                  <div style={{ fontSize: '16px', color: 'var(--text-light)', marginBottom: '4px', opacity: 0.8 }}>
                    📍 {user.city}, {user.state}
                  </div>
                )}
              </div>

              {/* Email (if public) */}
              {user.show_email_publicly && user.email && (
                <div className="profile-email-section" style={{ textAlign: 'right', minWidth: '200px' }}>
                  <div style={{ fontSize: '13px', color: 'var(--text-light)', marginBottom: '4px', opacity: 0.6 }}>Email</div>
                  <a href={`mailto:${user.email}`} style={{ fontSize: '15px', color: 'var(--accent-primary)', wordBreak: 'break-all' }}>
                    {user.email}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Two-Column Layout: Bio/Info (Left) | Gallery (Right) - ONLY BIO/INFO IS BOXED */}
          <div className="profile-two-column" style={{ display: 'grid', gridTemplateColumns: hasGalleryImages ? '1fr 1fr' : '1fr', gap: '24px', marginBottom: '40px' }}>
            
            {/* LEFT COLUMN: Bio and Role-Specific Info - WHITE BOX */}
            <div style={{ background: 'var(--bg-card)', borderRadius: '8px', padding: '32px', boxShadow: 'var(--shadow-card)' }}>
              
              {/* Bio */}
              {user.bio && (
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '700', color: 'var(--text-dark)' }}>About</h3>
                  <p className="profile-info-text" style={{ margin: 0, fontSize: '15px', lineHeight: '1.6', color: 'var(--text-dark)', whiteSpace: 'pre-wrap' }}>
                    {user.bio}
                  </p>
                </div>
              )}

              {/* Role-Specific Information */}
              <div>
                
                {/* BAND/ARTIST */}
                {user.account_role === 'band' && (
                  <>
                    {user.travel_distance && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Travel Radius</h4>
                        <div className="profile-info-text" className="profile-info-text" style={{ fontSize: '15px', color: 'var(--text-dark)' }}>
                          {user.travel_distance} miles
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* VENUE */}
                {user.account_role === 'venue' && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                      {user.venue_type && user.venue_type.length > 0 && (
                        <div>
                          <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Venue Type</h4>
                          <div className="profile-info-text" style={{ fontSize: '15px', color: 'var(--text-dark)' }}>
                            {formatVenueTypes(user.venue_type)}
                          </div>
                        </div>
                      )}

                      {user.age_restriction && (
                        <div>
                          <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Age Restriction</h4>
                          <div className="profile-info-text" style={{ fontSize: '15px', color: 'var(--text-dark)' }}>
                            {formatAgeRestriction(user.age_restriction)}
                          </div>
                        </div>
                      )}
                    </div>

                    {user.address && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Address</h4>
                        <div className="profile-info-text" style={{ fontSize: '15px', color: 'var(--text-dark)' }}>
                          {user.address}
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                      {user.capacity && (
                        <div>
                          <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Capacity</h4>
                          <div className="profile-info-text" style={{ fontSize: '15px', color: 'var(--text-dark)' }}>
                            {user.capacity} people
                          </div>
                        </div>
                      )}

                      {user.gear_available && (
                        <div>
                          <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Gear Available</h4>
                          <div className="profile-info-text" style={{ fontSize: '15px', color: 'var(--text-dark)' }}>
                            {user.gear_available}
                          </div>
                        </div>
                      )}
                    </div>

                    {user.address && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Address</h4>
                        <div className="profile-info-text" style={{ fontSize: '15px', color: 'var(--text-dark)' }}>
                          {user.address}
                        </div>
                      </div>
                    )}

                    {user.bookers_associated && user.bookers_associated.length > 0 && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Associated Promoters</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {user.bookers_associated.map((booker, idx) => (
                            <a 
                              key={idx} 
                              href={`/users/${booker.id}`}
                              style={{ fontSize: '15px', color: 'var(--accent-primary)' }}
                            >
                              {booker.name || booker.username}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* PHOTOGRAPHER, VENDOR, STUDIO */}
                {['photographer', 'vendor', 'studio'].includes(user.account_role) && (
                  <>
                    {user.travel_distance && user.account_role !== 'studio' && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Travel Radius</h4>
                        <div className="profile-info-text" style={{ fontSize: '15px', color: 'var(--text-dark)' }}>
                          {user.travel_distance} miles
                        </div>
                      </div>
                    )}

                    {user.services && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Services</h4>
                        <div className="profile-info-text" style={{ fontSize: '15px', color: 'var(--text-dark)', whiteSpace: 'pre-wrap' }}>
                          {user.services}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* PROMOTER */}
                {user.account_role === 'promoter' && (
                  <>
                    {user.travel_distance && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Travel Radius</h4>
                        <div className="profile-info-text" style={{ fontSize: '15px', color: 'var(--text-dark)' }}>
                          {user.travel_distance} miles
                        </div>
                      </div>
                    )}

                    {user.regions_covered && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Regions Covered</h4>
                        <div className="profile-info-text" style={{ fontSize: '15px', color: 'var(--text-dark)', whiteSpace: 'pre-wrap' }}>
                          {user.regions_covered}
                        </div>
                      </div>
                    )}

                    {user.venues_associated && user.venues_associated.length > 0 && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Associated Venues</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {user.venues_associated.map((venue, idx) => (
                            <a 
                              key={idx} 
                              href={`/users/${venue.id}`}
                              style={{ fontSize: '15px', color: 'var(--accent-primary)' }}
                            >
                              {venue.name || venue.username}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Links (all roles) */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {user.website_url && (
                    <div>
                      <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Website</h4>
                      <a href={user.website_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '15px', color: 'var(--accent-primary)' }}>
                        {user.website_url}
                      </a>
                    </div>
                  )}

                  {user.social_links && (
                    <div>
                      <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Social Links</h4>
                      <div className="profile-info-text" style={{ fontSize: '15px', color: 'var(--text-dark)', whiteSpace: 'pre-wrap' }}>
                        {user.social_links}
                      </div>
                    </div>
                  )}

                  {(user.instagram_url || user.facebook_url || user.tiktok_url) && (
                    <div>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Social Media</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {user.instagram_url && (
                          <a href={user.instagram_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '15px', color: 'var(--accent-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>📷</span> Instagram
                          </a>
                        )}
                        {user.facebook_url && (
                          <a href={user.facebook_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '15px', color: 'var(--accent-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>👥</span> Facebook
                          </a>
                        )}
                        {user.tiktok_url && (
                          <a href={user.tiktok_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '15px', color: 'var(--accent-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>🎵</span> TikTok
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {user.preferred_contact_method && (
                    <div>
                      <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Preferred Contact</h4>
                      <div style={{ fontSize: '15px', color: 'var(--text-dark)', textTransform: 'capitalize' }}>
                        {user.preferred_contact_method}
                      </div>
                    </div>
                  )}
                </div>

                {/* Genre Tags at Bottom (Bands only) */}
                {user.account_role === 'band' && user.genre_tags && user.genre_tags.length > 0 && (
                  <div className="profile-genre-tags" style={{ marginTop: 0 }}>
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Genres</h4>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {user.genre_tags.map((genre, idx) => (
                        <span key={idx} className="badge badge-outline" style={{ fontSize: '12px' }}>
                          {genre}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT COLUMN: Gallery (if exists) - NO BOX */}
            {hasGalleryImages && (
              <div className="profile-gallery-section">
                <h3 className="profile-gallery-hide-title" style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '700', color: 'var(--text-light)' }}>Gallery</h3>
                <Gallery images={user.image_gallery_urls} />
              </div>
            )}
          </div>

          {/* Posts Section (Full Width Below) - NO BOX */}
          {hasPosts && (
            <div>
              <h2 style={{ margin: '0 0 20px 0', fontSize: '24px', fontWeight: '700', color: 'var(--text-light)' }}>
                Posts
              </h2>
              <div className="profile-posts-grid" style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
                gap: '20px' 
              }}>
                {posts.map(post => (
                  <ProfilePostCard key={post.id} post={post} />
                ))}
              </div>
            </div>
          )}

          {/* No Posts Message */}
          {!hasPosts && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '16px', color: 'var(--text-light)', opacity: 0.6 }}>
                No posts yet
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PublicProfilePage;
